import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import {
  RefreshCw,
  Search,
  Phone,
  Mail,
  MapPin,
  Clock,
  Target,
  ChevronRight,
  X,
  Send,
  Calendar,
  ArrowRight,
  Users,
} from "lucide-react";

const STAGES = [
  {
    key: "new",
    label: "New",
    desc: "Just submitted",
    cls: "border-sky-300 bg-sky-50",
    pill: "bg-sky-100 text-sky-700",
  },
  {
    key: "not_connected",
    label: "Not Connected",
    desc: "Attempted, no contact yet",
    cls: "border-amber-300 bg-amber-50",
    pill: "bg-amber-100 text-amber-700",
  },
  {
    key: "meeting_done",
    label: "Meeting Done",
    desc: "Initial meeting completed",
    cls: "border-indigo-300 bg-indigo-50",
    pill: "bg-indigo-100 text-indigo-700",
  },
  {
    key: "awaiting_decision",
    label: "Awaiting Decision",
    desc: "Investor evaluating",
    cls: "border-purple-300 bg-purple-50",
    pill: "bg-purple-100 text-purple-700",
  },
  {
    key: "converted",
    label: "Converted",
    desc: "Onboarded as a client",
    cls: "border-emerald-400 bg-emerald-50",
    pill: "bg-emerald-100 text-emerald-700",
  },
  {
    key: "lost",
    label: "Lost",
    desc: "Did not convert",
    cls: "border-rose-300 bg-rose-50",
    pill: "bg-rose-100 text-rose-700",
  },
];

const GOAL_LABEL = {
  // Investor goals
  wealth_creation: "Wealth creation",
  retirement: "Retirement planning",
  tax_saving: "Tax saving",
  child_education: "Child's education",
  review: "Review existing portfolio",
  // MFD goals
  scale_practice: "Scale my practice",
  reporting: "Better client reporting",
  succession: "Succession / continuity planning",
  associates: "Manage associates / team",
  migrate: "Migrate from another platform",
  other: "Other",
};

const TIME_LABEL = {
  morning: "Morning (9–12)",
  afternoon: "Afternoon (12–4)",
  evening: "Evening (4–8)",
  weekend: "Weekends",
};

export default function AdminLeads() {
  const { api, user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null); // lead object opened in drawer

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data.leads || []);
      setCounts(data.stage_counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!q.trim()) return leads;
    const s = q.trim().toLowerCase();
    return leads.filter(
      (l) =>
        (l.full_name || "").toLowerCase().includes(s) ||
        (l.email || "").toLowerCase().includes(s) ||
        (l.mobile || "").toLowerCase().includes(s) ||
        (l.city || "").toLowerCase().includes(s)
    );
  }, [leads, q]);

  const byStage = useMemo(() => {
    const map = {};
    STAGES.forEach((s) => (map[s.key] = []));
    filtered.forEach((l) => {
      const k = STAGES.find((s) => s.key === l.status) ? l.status : "new";
      map[k].push(l);
    });
    return map;
  }, [filtered]);

  // Restrict at UI level too (in addition to the backend role check) so the
  // page chrome doesn't render for non-super-admins who somehow reach it.
  if (user && user.role !== "super_admin" && user.role !== "platform_owner") {
    return (
      <div
        className="p-8 max-w-xl bg-white border border-gray-200 rounded-xl"
        data-testid="admin-leads-forbidden"
      >
        <h1 className="text-lg font-medium text-gray-900">
          Lead management is restricted
        </h1>
        <p className="mt-2 text-sm text-gray-500">
          This page is available to Super Admins only.
        </p>
      </div>
    );
  }

  const updateLead = async (leadId, payload) => {
    try {
      await api.patch(`/admin/leads/${leadId}`, payload);
      toast.success("Lead updated");
      await load();
      // Refresh the drawer with latest data
      if (active && active.lead_id === leadId) {
        const refreshed = (await api.get("/admin/leads")).data.leads.find(
          (l) => l.lead_id === leadId
        );
        if (refreshed) setActive(refreshed);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div data-testid="admin-leads" className="space-y-8">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-light tracking-tight text-gray-900">
            Lead Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track investor meeting requests from the public site through the
            sales funnel.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="admin-leads-search"
              placeholder="Search name, email, mobile, city"
              className="pl-9 pr-4 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#1a3f3a] w-80"
            />
          </div>
          <button
            onClick={load}
            data-testid="admin-leads-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      {/* Funnel header — visualises drop-offs */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="text-xs uppercase tracking-[0.18em] text-gray-500 mb-3">
          Funnel
        </div>
        <div className="flex items-stretch gap-2">
          {STAGES.filter((s) => s.key !== "lost").map((s, i, arr) => (
            <div key={s.key} className="flex items-stretch flex-1 min-w-0">
              <FunnelStep
                stage={s}
                count={counts[s.key] || 0}
                isFirst={i === 0}
                isLast={i === arr.length - 1}
              />
              {i < arr.length - 1 && (
                <ArrowRight
                  size={16}
                  className="text-gray-300 self-center shrink-0 mx-1"
                />
              )}
            </div>
          ))}
          <div className="ml-3 self-center text-xs text-gray-400 border-l border-gray-200 pl-3">
            Lost
            <div className="text-rose-600 font-medium text-sm">
              {counts.lost || 0}
            </div>
          </div>
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4">
        {STAGES.map((s) => (
          <KanbanColumn
            key={s.key}
            stage={s}
            leads={byStage[s.key]}
            loading={loading}
            onOpen={(l) => setActive(l)}
          />
        ))}
      </div>

      {active && (
        <LeadDrawer
          lead={active}
          onClose={() => setActive(null)}
          onUpdate={(payload) => updateLead(active.lead_id, payload)}
        />
      )}
    </div>
  );
}

function FunnelStep({ stage, count, isFirst, isLast }) {
  return (
    <div
      data-testid={`funnel-step-${stage.key}`}
      className={`flex-1 min-w-0 rounded-lg border ${stage.cls} px-4 py-3 transition`}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-600">
        {stage.label}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-2xl font-light text-gray-900 tabular-nums">
          {count}
        </span>
        {!isFirst && !isLast && (
          <span className="text-[10px] text-gray-500">in stage</span>
        )}
      </div>
    </div>
  );
}

function KanbanColumn({ stage, leads, loading, onOpen }) {
  return (
    <div
      data-testid={`kanban-col-${stage.key}`}
      className="bg-white rounded-xl border border-gray-200 flex flex-col min-h-[400px]"
    >
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-gray-900">{stage.label}</div>
          <div className="text-[11px] text-gray-400">{stage.desc}</div>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${stage.pill}`}>
          {leads.length}
        </span>
      </div>
      <div className="flex-1 p-3 space-y-2 overflow-y-auto">
        {loading && (
          <div className="text-xs text-gray-400 text-center py-6">
            Loading…
          </div>
        )}
        {!loading && leads.length === 0 && (
          <div className="text-xs text-gray-400 text-center py-6 italic">
            No leads in this stage
          </div>
        )}
        {leads.map((l) => (
          <LeadCard key={l.lead_id} lead={l} onOpen={() => onOpen(l)} />
        ))}
      </div>
    </div>
  );
}

function LeadCard({ lead, onOpen }) {
  const isMfd = lead.lead_type === "mfd";
  return (
    <button
      onClick={onOpen}
      data-testid={`lead-card-${lead.lead_id}`}
      className="block w-full text-left bg-gray-50 hover:bg-white hover:border-[#C8A96B]/40 border border-gray-200 rounded-lg p-3 transition group"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded ${
                isMfd
                  ? "bg-[#1a3f3a] text-[#C8A96B]"
                  : lead.lead_type === "investor"
                  ? "bg-[#C8A96B]/15 text-[#7d6b3f] border border-[#C8A96B]/30"
                  : "bg-gray-100 text-gray-500"
              }`}
            >
              {isMfd ? "MFD" : lead.lead_type === "investor" ? "Investor" : "—"}
            </span>
            <span className="font-medium text-gray-900 text-sm truncate">
              {lead.full_name}
            </span>
          </div>
          <div className="text-[11px] text-gray-500 truncate mt-0.5">
            {isMfd && lead.firm_name ? `${lead.firm_name} · ` : ""}
            {lead.email}
          </div>
        </div>
        <ChevronRight
          size={14}
          className="text-gray-300 group-hover:text-[#C8A96B] shrink-0 mt-1"
        />
      </div>
      <div className="mt-3 flex items-center gap-1.5 flex-wrap text-[10px]">
        {lead.mobile && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white text-gray-600 border border-gray-200">
            <Phone size={9} /> {lead.mobile}
          </span>
        )}
        {lead.city && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white text-gray-600 border border-gray-200">
            <MapPin size={9} /> {lead.city}
          </span>
        )}
        {lead.arn_number && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white text-gray-600 border border-gray-200">
            ARN · {lead.arn_number}
          </span>
        )}
        {lead.goal && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#C8A96B]/10 text-[#7d6b3f] border border-[#C8A96B]/30">
            <Target size={9} /> {GOAL_LABEL[lead.goal] || lead.goal}
          </span>
        )}
      </div>
      <div className="mt-2 text-[10px] text-gray-400">
        Submitted{" "}
        {lead.created_at
          ? new Date(lead.created_at).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : ""}
      </div>
    </button>
  );
}

function LeadDrawer({ lead, onClose, onUpdate }) {
  const [note, setNote] = useState("");
  return (
    <div
      data-testid="lead-drawer"
      className="fixed inset-0 z-40 flex"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex-1 bg-black/40" />
      <aside className="w-full max-w-md bg-white border-l border-gray-200 h-full overflow-y-auto shadow-2xl">
        <header className="px-6 py-5 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-gray-400 flex items-center gap-2">
              Lead
              {lead.lead_type && (
                <span
                  className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded ${
                    lead.lead_type === "mfd"
                      ? "bg-[#1a3f3a] text-[#C8A96B]"
                      : "bg-[#C8A96B]/15 text-[#7d6b3f] border border-[#C8A96B]/30"
                  }`}
                >
                  {lead.lead_type === "mfd" ? "MFD" : "Investor"}
                </span>
              )}
            </div>
            <h2 className="mt-1 text-xl font-medium text-gray-900">
              {lead.full_name}
            </h2>
            {lead.firm_name && (
              <div className="text-xs text-gray-500 mt-0.5">
                {lead.firm_name}
                {lead.arn_number ? ` · ARN ${lead.arn_number}` : ""}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            data-testid="lead-drawer-close"
            className="text-gray-400 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </header>

        <section className="px-6 py-5 space-y-3 border-b border-gray-100">
          <Row icon={Mail} value={lead.email} />
          <Row icon={Phone} value={lead.mobile} />
          {lead.city && <Row icon={MapPin} value={lead.city} />}
          {lead.goal && (
            <Row icon={Target} value={GOAL_LABEL[lead.goal] || lead.goal} />
          )}
          {lead.preferred_time && (
            <Row
              icon={Clock}
              value={TIME_LABEL[lead.preferred_time] || lead.preferred_time}
            />
          )}
          {lead.created_at && (
            <Row
              icon={Calendar}
              value={`Submitted ${new Date(lead.created_at).toLocaleString("en-IN", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}`}
            />
          )}
          {lead.message && (
            <div className="mt-2 bg-gray-50 border border-gray-100 rounded-lg p-3 text-sm text-gray-700 leading-relaxed">
              {lead.message}
            </div>
          )}
        </section>

        <section className="px-6 py-5 border-b border-gray-100">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-3">
            Stage
          </div>
          <div className="grid grid-cols-2 gap-2">
            {STAGES.map((s) => {
              const isCurrent = lead.status === s.key;
              return (
                <button
                  key={s.key}
                  data-testid={`lead-stage-${s.key}`}
                  onClick={() => !isCurrent && onUpdate({ status: s.key })}
                  className={`text-xs px-3 py-2 rounded-lg border transition text-left ${
                    isCurrent
                      ? `${s.cls} font-medium`
                      : "border-gray-200 bg-white hover:border-[#C8A96B]/40 text-gray-700"
                  }`}
                >
                  {s.label}
                  {isCurrent && (
                    <span className="ml-1 text-[10px] uppercase">· current</span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        <section className="px-6 py-5 border-b border-gray-100">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-3">
            Add a note
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            data-testid="lead-add-note"
            rows={3}
            placeholder="Call attempted at 4:30 PM — call back tomorrow…"
            className="block w-full bg-white border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:border-[#1a3f3a] resize-none"
          />
          <button
            onClick={() => {
              if (!note.trim()) return;
              onUpdate({ note });
              setNote("");
            }}
            data-testid="lead-save-note"
            disabled={!note.trim()}
            className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#1a3f3a] text-white text-sm font-medium hover:bg-[#0e2024] transition disabled:opacity-50"
          >
            <Send size={13} /> Save note
          </button>
        </section>

        <section className="px-6 py-5">
          <div className="text-xs uppercase tracking-[0.18em] text-gray-400 mb-3 flex items-center gap-2">
            <Users size={11} /> Activity
          </div>
          <ol className="space-y-3">
            {(lead.history || []).slice().reverse().map((h, i) => (
              <li
                key={i}
                className="text-xs text-gray-600 flex items-baseline gap-3"
              >
                <span className="text-gray-400 tabular-nums">
                  {new Date(h.at).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span>
                  Moved {h.from ? <em>{h.from}</em> : "to"}
                  {h.from ? " → " : " "}
                  <span className="font-medium text-gray-900">{h.to}</span>
                  <span className="text-gray-400"> · {h.by || "system"}</span>
                </span>
              </li>
            ))}
            {(lead.notes || []).slice().reverse().map((n, i) => (
              <li
                key={`n${i}`}
                className="text-xs flex flex-col gap-1 border-l-2 border-[#C8A96B]/40 pl-3 py-1"
              >
                <span className="text-gray-400 tabular-nums">
                  {new Date(n.at).toLocaleString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  · {n.by}
                </span>
                <span className="text-gray-700">{n.text}</span>
              </li>
            ))}
          </ol>
        </section>
      </aside>
    </div>
  );
}

function Row({ icon: Icon, value }) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-700">
      <Icon size={14} className="text-gray-400 shrink-0" />
      <span className="truncate">{value}</span>
    </div>
  );
}
